import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { io } from "socket.io-client";
import { SOCKET_URL } from "constants/routes/api";
import keys from "constants/keys";
import { getData } from "utility";
import { setDeviceSyncLog } from "redux/Devices/actions";
import { eventLogs } from "utility/deviceDetailUtils";
import moment from "moment";

// Global socket instance to prevent duplicate connections
let globalSocket = null;
let globalListeners = [];
let connectionCount = 0;

/**
 * Custom hook to manage device socket connection
 * Ensures only one socket connection exists across all components
 * Matches React Native app implementation
 */
// Global dispatch ref to avoid closure issues with global socket
let globalDispatchRef = null;

export const useDeviceSocket = (onNotificationReceived) => {
  const userData = useSelector((store) => store?.auth?.userData);
  const dispatch = useDispatch();
  const listenerRef = useRef(null);
  const socketRef = useRef(null);
  const onNotificationRef = useRef(onNotificationReceived);
  
  // Update global dispatch ref
  useEffect(() => {
    globalDispatchRef = dispatch;
  }, [dispatch]);

  // Keep callback ref updated
  useEffect(() => {
    onNotificationRef.current = onNotificationReceived;
  }, [onNotificationReceived]);

  useEffect(() => {
    const initializeSocket = async () => {
      try {
        const token = await getData(keys?.adminAccessTokenKey);
        if (!token) return;

        // If socket already exists and is connected, reuse it
        if (globalSocket && globalSocket?.connected) {
          socketRef.current = globalSocket;
          connectionCount++;
        } else {
          // Create new socket connection
          const socket = io(SOCKET_URL, {
            auth: {
              token: token,
              isadminrequest: true,
            },
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
          });

          globalSocket = socket;
          socketRef.current = socket;
          connectionCount = 1;

          socket.on("connect", () => {
            console.log("✅ Connected to server with socket ID:", socket?.id);
          });

          socket.on("ping", () => {
            console.log("📡 Received custom ping from server");
            socket?.emit("pong");
          });

          socket.on("disconnect", (reason) => {
            console.log("🔌 Socket disconnected:", JSON.stringify(reason));
            // Set sync log when socket disconnects
            if (globalDispatchRef) {
              setDeviceSyncLog(globalDispatchRef, {
                log: eventLogs?.socket_disconnect,
                loggedAt: moment()?.utc()?.toISOString(),
              });
            }
          });

          socket.on("error", (error) => {
            console.error("❗Socket error:", error);
            // Set sync log when socket error occurs
            if (globalDispatchRef) {
              setDeviceSyncLog(globalDispatchRef, {
                log: eventLogs?.socket_error,
                loggedAt: moment()?.utc()?.toISOString(),
              });
            }
          });

          socket.on("unauthorized", (msg) => {
            console.error("🚫 Unauthorized:", msg);
          });

          // Global notification handler that distributes to all listeners
          socket.on("notification", (data) => {
            console.log("📨 Message from server:", data);
            // Notify global listeners
            globalListeners?.forEach((listener) => {
              try {
                listener(data);
              } catch (error) {
                console.error("Error in socket notification listener:", error);
              }
            });
          });
        }

        // Add this component's listener
        if (onNotificationReceived) {
          const currentListener = (data) => {
            if (onNotificationRef?.current) {
              onNotificationRef?.current(data);
            }
          };
          listenerRef.current = currentListener;
          globalListeners?.push(currentListener);
        }
      } catch (error) {
        console.error("Failed to initialize socket:", error);
      }
    };

    initializeSocket();

    // Cleanup function
    return () => {
      // Remove this component's listener
      if (listenerRef?.current) {
        globalListeners = globalListeners?.filter(
          (listener) => listener !== listenerRef?.current
        );
        listenerRef.current = null;
      }

      // Decrement connection count
      connectionCount--;

      // Only disconnect if no components are using the socket
      if (connectionCount <= 0 && globalSocket) {
        console.log("🔌 Disconnecting socket - no active components");
        globalSocket?.off();
        globalSocket?.disconnect();
        globalSocket = null;
        globalListeners = [];
        connectionCount = 0;
      }
    };
  }, [userData]);

  return socketRef?.current;
};

